import { FormProvider, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";

import { round } from "@repo/system";
import { priceFormatter } from "~/utils/price-formatter";
import { T, useT } from "@repo/transifex";
import type { Product } from "@repo/types";
import { type PosProduct } from "~/types/product";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { Button, Dialog } from "~/components";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { basketActions } from "~/providers/store/basket";
import { modalVariantsActions } from "~/providers/store/modal-variants";
import {
  stampcardActions,
  useStampcardState
} from "~/providers/store/stampcard";
import { useBasketContext } from "~/providers/store/basket/context";
import { type StampCardDiscount } from "~/api/products/types";
import {
  resolveStampCardBaseDiscount,
  resolveStampCardDiscountedPrice
} from "~/lib/sale-engine";

import { computeStampcardFormValues } from "./compute-form-values";
import { ProductRadioButton } from "./ProductRadioButton";
import { StampCardSection } from "./StampCardSection";

type StampCardFormType = Record<
  NonNullable<StampCardDiscount["stampCardRuleId"]>,
  Product["id"]
>;

const StampcardPage = () => {
  const t = useT();

  const navigate = useNavigate();

  const { locale, currency } = useCurrentTillLocalisation();

  const { products: stampcardProducts } = useStampcardState();

  const { data: userProducts } = useUserProductsPos();

  const products = useBasketContext((state) => state.products);
  const productsInBasket = useBasketContext((state) => state.productsInBasket);

  const methods = useForm<StampCardFormType>({
    values: computeStampcardFormValues(stampcardProducts, productsInBasket)
  });

  const { handleSubmit, watch, register } = methods;

  const handleOnClose = () => {
    basketActions.removeRedeemed();
    stampcardActions.reset();
    navigate(-1);
  };

  const onSubmit = (data: StampCardFormType) => {
    Object.entries(data)
      .filter(([_, productId]) => Boolean(productId))
      .forEach(([stampCardRuleId, productId]) => {
        if (!productId) throw new Error("Product not selected");

        const stampcard = userProducts?.stampCardDiscounts?.find(
          (stampCard) => stampCard.stampCardRuleId === stampCardRuleId
        );
        const product = products.find((product) => product.id === productId);

        if (!stampcard) throw new Error("Stampcard not found");
        if (!product) throw new Error("Product not found");

        const discountUnitPrice = round(
          resolveStampCardBaseDiscount({
            baseUnitPriceInclVat: product.unitPrice,
            discountType: stampcard.discountType,
            discount: stampcard.discount
          }),
          2
        );

        if (stampcardProducts) {
          const steps = stampcardProducts.find(
            (stampcardProduct) => stampcardProduct.productId === product.id
          )?.steps;

          if (steps && steps.length > 0) {
            basketActions.addRedeemed(
              productId,
              discountUnitPrice,
              stampCardRuleId,
              steps
            );
          } else {
            basketActions.addRedeemed(
              productId,
              discountUnitPrice,
              stampCardRuleId
            );
          }
        }

        stampcardActions.setProductStampCardRuleId({
          stampCardRuleId,
          productId
        });
      });

    navigate(-1);
  };

  const formSections = mapStampCardDiscountsWithRedeemableProducts(
    products,
    userProducts?.stampCardDiscounts ?? []
  );

  if (
    formSections.length === 0 ||
    !formSections.some((section) => section.redeemableProducts.length > 0)
  ) {
    return <Navigate to=".." />;
  }

  return (
    <Dialog
      onOutsideClick={() => {
        navigate("..");
      }}
      size="lg"
    >
      <div className="flex flex-col justify-center p-8">
        <p className="mb-2 whitespace-pre text-center text-2xl font-bold">
          <T _str="You have a reward!" />
          <br />
          <T _str="Do you want to redeem it now?" />
        </p>
        <FormProvider {...methods}>
          <form className="max-h-96 w-full overflow-y-scroll">
            {formSections.map((section) => (
              <StampCardSection
                discount={t("{discount} off 1 selected product", {
                  discount:
                    section.stampCardDiscount.discountType === "percentage"
                      ? `${section.stampCardDiscount.discount} %`
                      : priceFormatter({
                          value: section.stampCardDiscount.discount,
                          currency,
                          locale
                        })
                })}
                key={section.stampCardDiscount.stampCardRuleId}
              >
                {section.redeemableProducts.map((product) => {
                  const match = stampcardProducts?.find(
                    (stampcardProduct) =>
                      stampcardProduct.productId === product.id
                  );
                  const hasSteps = Boolean(
                    match?.steps && match.steps.length > 0
                  );
                  const isSelected = Boolean(match);

                  return (
                    <ProductRadioButton
                      onClick={() => {
                        if (!hasSteps && product.options.length > 0) {
                          modalVariantsActions.init({
                            productId: product.id,
                            options: product.options.map((option) => option.id)
                          });

                          navigate(
                            `/pos/product/${product.id}?stampCardRuleId=${section.stampCardDiscount.stampCardRuleId}`
                          );
                        }

                        if (!isSelected) {
                          stampcardActions.setProduct({
                            productId: product.id,
                            steps: undefined,
                            forRule:
                              section.stampCardDiscount.stampCardRuleId ??
                              undefined
                          });
                        }
                      }}
                      displayPrice={product.displayPrice}
                      displayPriceDiscounted={priceFormatter({
                        value: resolveStampCardDiscountedPrice({
                          baseUnitPriceInclVat: product.unitPrice,
                          discountType: section.stampCardDiscount.discountType,
                          discount: section.stampCardDiscount.discount
                        }),
                        currency,
                        locale
                      })}
                      id={product.id}
                      key={product.id}
                      label={product.name}
                      checked={isSelected}
                      {...register(
                        section.stampCardDiscount.stampCardRuleId ?? "unknown"
                      )}
                    />
                  );
                })}
              </StampCardSection>
            ))}
          </form>
        </FormProvider>
      </div>

      <div className="grid w-full grid-cols-2 gap-4 border-t p-4">
        <Button onClick={handleOnClose} variant="secondary">
          <T _str="Not now" />
        </Button>
        <Button
          disabled={Object.values(watch()).every((value) => !value)}
          onClick={handleSubmit(onSubmit)}
          type="submit"
        >
          <T _str="Redeem reward" />
        </Button>
      </div>
    </Dialog>
  );
};

const mapStampCardDiscountsWithRedeemableProducts = (
  products: PosProduct[],
  stampCardDiscounts: StampCardDiscount[]
) => {
  return stampCardDiscounts.map((stampCardDiscount) => {
    const redeemableProducts = products.filter((product) => {
      // variant-weight not supported
      if (product.unitSystem && product.unitSystem !== "piece") return false;

      return stampCardDiscount.productIds.includes(product.id);
    });

    return {
      stampCardDiscount,
      redeemableProducts
    };
  });
};

export { StampcardPage };
